import { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import Header from './components/Header';
import Footer from './components/Footer';
import ErrorBoundary from './components/ErrorBoundary';
import PageLoader from './components/PageLoader';
import Home from './pages/Home';
import { EmailVerifyBanner } from './components/EmailVerifyBanner';
import { LoginQueryHandler } from './components/LoginQueryHandler';
import { StudioLaunchQueryHandler } from './components/studio/StudioLaunchQueryHandler';
import { LogoutQueryHandler } from './components/LogoutQueryHandler';
import ProtectedRoute from './components/ProtectedRoute';
import LocalhostOnlyRoute from './components/LocalhostOnlyRoute';
import { GetStartedFlowProvider } from './components/getstarted/GetStartedFlowContext';
import { PendingActionsProvider } from './context/PendingActionsContext';
import { useApp } from './context/AppContext';
import ScrollToTop from './components/ScrollToTop';
import { useAnalytics } from './hooks/useAnalytics';
import { useUTMTracking } from './hooks/useUTMTracking';
import { captureQueryParams } from './utils/queryParamStore';
import { DEFAULT_SAMPLE_TOPIC_KEY } from './data/sampleStudyMaterials/constants';
import { getAllPlans } from './services/learningPlanService';

const LoginModal = lazy(() => import('./components/LoginModal'));
const VerifyEmailModal = lazy(() => import('./components/auth/VerifyEmailModal'));
const CookieConsentBanner = lazy(() => import('./components/CookieConsentBanner'));

function DeferredCookieConsentBanner() {
  const [ready, setReady] = useState(
    () => typeof navigator !== 'undefined' && !!navigator.webdriver,
  );
  useEffect(() => {
    if (ready) return;
    let done = false;
    const enable = () => {
      if (done) return;
      done = true;
      cleanup();
      setReady(true);
    };
    const onInput = () => enable();
    const events = ['pointerdown', 'keydown', 'touchstart', 'scroll'] as const;
    for (const type of events) {
      window.addEventListener(type, onInput, { once: true, passive: true });
    }
    const fallback = window.setTimeout(enable, 10000);
    const cleanup = () => {
      window.clearTimeout(fallback);
      for (const type of events) window.removeEventListener(type, onInput);
    };
    return cleanup;
  }, [ready]);
  if (!ready) return null;
  return (
    <Suspense fallback={null}>
      <CookieConsentBanner />
    </Suspense>
  );
}

function createLazyRouteComponents() {
  return {
    PlanLanding: lazy(() => import('./pages/PlanLanding')),
    LandingPageLp: lazy(() => import('./pages/LandingPageLp')),
    UpskillPlan: lazy(() => import('./pages/UpskillPlan')),
    PrivacyPolicy: lazy(() => import('./pages/PrivacyPolicy')),
    TermsOfUse: lazy(() => import('./pages/TermsOfUse')),
    RefundPolicy: lazy(() => import('./pages/RefundPolicy')),
    Profile: lazy(() => import('./pages/Profile')),
    AuthCallback: lazy(() => import('./pages/AuthCallback')),
    ForgotPassword: lazy(() => import('./pages/ForgotPassword')),
    ResetPassword: lazy(() => import('./pages/ResetPassword')),
    HowItWorks: lazy(() => import('./pages/HowItWorks')),
    VerifyEmail: lazy(() => import('./pages/VerifyEmail')),
    Studio: lazy(() => import('./pages/Studio')),
    Pricing: lazy(() => import('./pages/Pricing')),
    FAQ: lazy(() => import('./pages/FAQ')),
    About: lazy(() => import('./pages/About')),
    Contact: lazy(() => import('./pages/Contact')),
    StudyMaterial: lazy(() => import('./pages/StudyMaterial')),
    SamplePlan: lazy(() => import('./pages/SamplePlan')),
    SampleStudyMaterial: lazy(() => import('./pages/SampleStudyMaterial')),
    MyPlans: lazy(() => import('./pages/MyPlans')),
    Blog: lazy(() => import('./pages/Blog')),
    BlogArticle: lazy(() => import('./pages/BlogArticle')),
    Learn: lazy(() => import('./pages/Learn')),
    LearnModule: lazy(() => import('./pages/LearnModule')),
    LearnLesson: lazy(() => import('./pages/LearnLesson')),
    GlossaryIndex: lazy(() => import('./pages/GlossaryIndex')),
    GlossaryArticle: lazy(() => import('./pages/GlossaryArticle')),
    BuilderPortfolioRoute: lazy(() => import('./pages/BuilderPortfolioRoute')),
    CertificateVerifyRoute: lazy(() => import('./pages/CertificateVerifyRoute')),
    AiForSoftwareEngineers: lazy(() => import('./pages/AiForSoftwareEngineers')),
    EmailMetricsDashboard: lazy(() => import('./pages/EmailMetricsDashboard')),
    NotFound: lazy(() => import('./pages/NotFound')),
  };
}

interface UpskillPlanLocationState {
  plan?: unknown;
  planId?: number;
}

/** /upskill-plan dispatcher: guests and users without plans see the public landing;
 *  signed-in users with at least one plan (or explicit plan navigation state) see the plan result. */
function UpskillPlanGate({
  Landing,
  Result,
}: {
  Landing: React.ComponentType;
  Result: React.ComponentType;
}) {
  const { isLoggedIn, userTier, loading: sessionLoading } = useApp();
  const location = useLocation();
  const locationState = location.state as UpskillPlanLocationState | null;
  const hasExplicitPlan = !!(locationState?.plan || locationState?.planId);
  const authed = isLoggedIn && userTier !== 'guest';

  const [hasSavedPlans, setHasSavedPlans] = useState<boolean | null>(null);

  useEffect(() => {
    if (hasExplicitPlan) {
      setHasSavedPlans(true);
      return;
    }

    if (sessionLoading) {
      setHasSavedPlans(null);
      return;
    }

    if (!authed) {
      setHasSavedPlans(false);
      return;
    }

    let cancelled = false;
    setHasSavedPlans(null);

    getAllPlans()
      .then((data) => {
        if (!cancelled) setHasSavedPlans(data.plans.length > 0);
      })
      .catch(() => {
        if (!cancelled) setHasSavedPlans(false);
      });

    return () => {
      cancelled = true;
    };
  }, [authed, hasExplicitPlan, sessionLoading, location.key]);

  if (hasExplicitPlan || (authed && hasSavedPlans === true)) {
    return <Result />;
  }

  if (sessionLoading || (authed && hasSavedPlans === null)) {
    return <PageLoader />;
  }

  return <Landing />;
}

function AppRoutes({ lazyRetryKey }: { lazyRetryKey: number }) {
  const { search } = useLocation();
  captureQueryParams(search);
  useAnalytics();
  useUTMTracking();

  const L = useMemo(
    () => createLazyRouteComponents(),
    // New lazy() instances when user retries after a failed chunk load (e.g. offline).
    [lazyRetryKey]
  );

  return (
    <>
    <ScrollToTop />
    <Suspense fallback={<PageLoader />}>
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/lp" element={<L.LandingPageLp />} />
      <Route path="/auth-callback" element={<L.AuthCallback />} />
      <Route path="/forgot-password" element={<L.ForgotPassword />} />
      <Route path="/reset-password" element={<L.ResetPassword />} />
      <Route path="/verify-email" element={<L.VerifyEmail />} />
      <Route path="/payment" element={<Navigate to="/pricing" replace />} />
      {/* /upskill-plan is dual-purpose: public landing for guests and users without plans,
          latest/specific plan for signed-in users who have saved plans (Mock 5). */}
      <Route path="/upskill-plan" element={<UpskillPlanGate Landing={L.PlanLanding} Result={L.UpskillPlan} />} />
      <Route path="/privacy-policy" element={<L.PrivacyPolicy />} />
      <Route path="/terms-of-use" element={<L.TermsOfUse />} />
      <Route path="/refund-policy" element={<L.RefundPolicy />} />
      <Route path="/how-it-works" element={<L.HowItWorks />} />
      <Route path="/studio" element={<L.Studio />} />
      <Route path="/pricing" element={<L.Pricing />} />
      <Route path="/faq" element={<L.FAQ />} />
      <Route path="/about" element={<L.About />} />
      <Route path="/contact" element={<L.Contact />} />
      <Route path="/sample-plan" element={<L.SamplePlan />} />
      <Route
        path="/sample-study-material"
        element={<Navigate to={`/sample-study-material/${DEFAULT_SAMPLE_TOPIC_KEY}`} replace />}
      />
      <Route path="/sample-study-material/:topicKey" element={<L.SampleStudyMaterial />} />
      <Route path="/profile" element={<L.Profile />} />
      <Route path="/study-material/:planId/:itemId" element={<ProtectedRoute><L.StudyMaterial /></ProtectedRoute>} />
      <Route path="/my-plans" element={<L.MyPlans />} />
      <Route path="/blog" element={<L.Blog />} />
      <Route path="/blog/:slug" element={<L.BlogArticle />} />
      <Route path="/learn" element={<L.Learn />} />
      <Route path="/learn/:moduleSlug" element={<L.LearnModule />} />
      <Route path="/learn/:moduleSlug/:lessonSlug" element={<L.LearnLesson />} />
      <Route path="/glossary" element={<L.GlossaryIndex />} />
      <Route path="/glossary/:slug" element={<L.GlossaryArticle />} />
      <Route path="/u/:username" element={<L.BuilderPortfolioRoute />} />
      <Route path="/cert/:id" element={<L.CertificateVerifyRoute />} />
      <Route path="/ai-for-software-engineers" element={<L.AiForSoftwareEngineers />} />
      {/* Internal, localhost-only email outreach metrics dashboard (not linked in nav). */}
      <Route path="/email-metrics" element={<LocalhostOnlyRoute><L.EmailMetricsDashboard /></LocalhostOnlyRoute>} />
      <Route path="*" element={<L.NotFound />} />
    </Routes>
    </Suspense>
    </>
  );
}

function AuthRedirectHandler() {
  const location = useLocation();
  const navigate = useNavigate();
  const { openLoginModal } = useApp();

  useEffect(() => {
    if (location.state?.authRequired) {
      openLoginModal('Please sign in to continue');
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate, openLoginModal]);

  return null;
}

function App() {
  const location = useLocation();
  const [lazyRetryKey, setLazyRetryKey] = useState(0);
  const { isLoginModalOpen, isVerifyEmailModalOpen } = useApp();

  useEffect(() => {
    setLazyRetryKey(0);
  }, [location.pathname]);

  return (
    <div className="app" style={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      <PendingActionsProvider>
      <GetStartedFlowProvider>
        <Header />
        <EmailVerifyBanner />
        <main id="main-content" style={{ flex: 1 }}>
          <ErrorBoundary
            key={`${location.pathname}-${lazyRetryKey}`}
            onRetry={() => setLazyRetryKey((k) => k + 1)}
          >
            <AppRoutes lazyRetryKey={lazyRetryKey} />
          </ErrorBoundary>
        </main>
        <Footer />
        {isLoginModalOpen && (
          <Suspense fallback={null}>
            <LoginModal />
          </Suspense>
        )}
        {isVerifyEmailModalOpen && (
          <Suspense fallback={null}>
            <VerifyEmailModal />
          </Suspense>
        )}
        <LoginQueryHandler />
        <StudioLaunchQueryHandler />
        <LogoutQueryHandler />
        <AuthRedirectHandler />
        <DeferredCookieConsentBanner />
      </GetStartedFlowProvider>
      </PendingActionsProvider>
    </div>
  );
}

export default App;
